import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { invitationToken } from '../../core/http/invitation-link';
import { GameService } from '../../core/state/game.service';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { Language } from '../../core/i18n/translations';

function passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const repeatPassword = group.get('repeatPassword')?.value;
  if (!password || !repeatPassword) {
    return null;
  }
  return password === repeatPassword ? null : { passwordMismatch: true };
}

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, IconComponent],
  templateUrl: './register.component.html',
  styleUrl: './login.component.scss'
})
export class RegisterComponent implements OnInit, OnDestroy {
  readonly game = inject(GameService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly isSettingsMenuOpen = signal<boolean>(false);
  readonly isSubmitting = signal<boolean>(false);
  readonly isResending = signal<boolean>(false);
  readonly isRegisteredSuccess = signal<boolean>(false);
  readonly resendCooldown = signal<number>(0);
  readonly registeredEmail = signal<string>('');
  readonly errorMessage = signal<string | null>(null);
  readonly resendFeedback = signal<string | null>(null);

  private cooldownTimer: any = null;

  readonly registerForm = this.fb.group({
    email: ['', [Validators.required, Validators.email, Validators.maxLength(254)]],
    password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(128)]],
    repeatPassword: ['', [Validators.required]],
  }, { validators: [passwordMatchValidator] });

  ngOnInit(): void {
    const fragment = this.route.snapshot.fragment || '';
    const token = invitationToken(`/leagues#${fragment}`);
    if (token && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('lom_pending_invitation', token);
    }
  }

  ngOnDestroy(): void {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }

  toggleSettingsMenu(): void {
    this.isSettingsMenuOpen.update((v) => !v);
  }

  closeSettingsMenu(): void {
    this.isSettingsMenuOpen.set(false);
  }

  selectLanguage(lang: Language): void {
    this.game.i18n.setLanguage(lang);
  }

  get invitationFragment(): string | undefined {
    return this.route.snapshot.fragment || undefined;
  }

  startResendCooldown(seconds: number = 60): void {
    this.resendCooldown.set(seconds);
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
    }
    this.cooldownTimer = setInterval(() => {
      this.resendCooldown.update((v) => {
        if (v <= 1) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
          return 0;
        }
        return v - 1;
      });
    }, 1000);
  }

  async handleRegister(): Promise<void> {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    if (this.isSubmitting()) return;

    this.errorMessage.set(null);
    this.resendFeedback.set(null);

    const { email, password } = this.registerForm.getRawValue();
    if (!email || !password) return;

    this.isSubmitting.set(true);
    try {
      // Password must NOT be trimmed
      await this.game.register({
        email: email.trim(),
        password: password,
      });

      this.registeredEmail.set(email.trim());
      this.isRegisteredSuccess.set(true);
      this.startResendCooldown(60);
    } catch (err: any) {
      this.handleError(err);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async handleResend(): Promise<void> {
    const email = this.registeredEmail();
    if (!email || this.resendCooldown() > 0 || this.isResending()) return;

    this.isResending.set(true);
    this.resendFeedback.set(null);
    try {
      await this.game.resendRegistration({ email });
      this.resendFeedback.set(this.game.t('auth.resend_sent'));
      this.startResendCooldown(60);
    } catch (err: any) {
      if (err?.status === 429) {
        const cooldown = err.retryAfter || 60;
        this.startResendCooldown(cooldown);
        this.resendFeedback.set(this.game.t('auth.err_rate_limited'));
      } else if (err?.status === 503) {
        this.resendFeedback.set(this.game.t('auth.err_unavailable'));
      } else {
        this.resendFeedback.set(this.game.t('auth.err_unavailable'));
      }
    } finally {
      this.isResending.set(false);
    }
  }

  private handleError(err: any): void {
    if (err?.status === 400) {
      this.errorMessage.set(this.game.t('auth.err_invalid_request'));
    } else if (err?.status === 404) {
      this.errorMessage.set(this.game.t('auth.err_registration_disabled'));
    } else if (err?.status === 429) {
      this.errorMessage.set(this.game.t('auth.err_rate_limited'));
    } else if (err?.status === 503) {
      this.errorMessage.set(this.game.t('auth.err_unavailable'));
    } else {
      this.errorMessage.set(err?.message || this.game.t('auth.err_unavailable'));
    }
  }
}
